import React from 'react';
import { ArrowLeftRightIcon, FileTextIcon, SparklesIcon, UserIcon } from 'lucide-react';
import { Band, PullQuote } from './Band';
import { sessionSteps } from '../../data/site';
const asymmetry = [{
  icon: SparklesIcon,
  title: 'The AI assistant',
  has: ['Full frontier reasoning', 'Domain expertise', 'Unlimited use, encouraged'],
  lacks: ['The source documents', 'Entity history', 'Prior-period treatments', 'Internal conventions']
}, {
  icon: FileTextIcon,
  title: 'The source files',
  has: ['Ledger, schedules, brief', 'Six scenarios, two of them clean', 'Explanations left unstated'],
  lacks: ['Any route to the model, unless the candidate carries it there']
}];
const phaseWidths = ['4%', '6%', '78%', '12%'];
export function HowItWorks() {
  return <Band id="how" eyebrow="How it works" title="The model is expert. It just isn't informed." description="Throttling the AI measures patience, not skill. So the assistant stays capable and stays blind to the evidence. Every decision the candidate makes to bridge that gap is recorded." aside={<PullQuote data-id="element-221" data-mp="5lcmo6mkifdi">
          The candidate is the only route between a model that can reason and
          the documents that settle the question.
        </PullQuote>} data-id="element-220" data-mp="ov68lba26xdk">
      <div className="grid gap-4 lg:grid-cols-[1fr_auto_1fr] lg:items-stretch" data-id="element-222" data-mp="fi7kenb0obqq">
        {asymmetry.map((col, i) => <React.Fragment key={col.title} data-id="element-223" data-mp="agry6rx3rz6r" data-mpi={i}>
            <figure className="rounded-2xl border border-mist bg-white/70 p-7" data-id="element-224" data-mp="oi4dd14fvwrl">
              <figcaption className="flex items-center gap-3" data-id="element-225" data-mp="og1h3zcp17ef">
                <col.icon className="h-5 w-5 text-teal" aria-hidden="true" data-id="element-226" data-mp="ji9m2wgr5560" />
                <span className="font-display text-xl text-ink" data-id="element-227" data-mp="y874swvwx2nt">{col.title}</span>
              </figcaption>
              <dl className="mt-6 space-y-5" data-id="element-228" data-mp="t9vukl1pgq52">
                <div data-id="element-229" data-mp="taxfricfqt0i">
                  <dt className="font-mono text-xs uppercase tracking-[0.14em] text-teal" data-id="element-230" data-mp="178cbt5x71z1g">
                    Has
                  </dt>
                  <dd className="mt-2 flex flex-wrap gap-2" data-id="element-231" data-mp="138bmgq8k43fm">
                    {col.has.map(item => <span key={item} className="rounded-lg border border-teal/25 bg-teal/5 px-3 py-1.5 text-sm text-ink" data-id="element-232" data-mp="1ab01lyemz56k">
                        {item}
                      </span>)}
                  </dd>
                </div>
                <div data-id="element-233" data-mp="1k6tn47ac290h">
                  <dt className="font-mono text-xs uppercase tracking-[0.14em] text-clay" data-id="element-234" data-mp="1k6bhwl5p0kgf">
                    Lacks
                  </dt>
                  <dd className="mt-2 flex flex-wrap gap-2" data-id="element-235" data-mp="1t18208qybhj2">
                    {col.lacks.map(item => <span key={item} className="rounded-lg border border-mist bg-bone px-3 py-1.5 text-sm text-graphite line-through decoration-clay/40" data-id="element-236" data-mp="1xzjaaf1lljz6">
                        {item}
                      </span>)}
                  </dd>
                </div>
              </dl>
            </figure>

            {i === 0 ? <div className="flex flex-col items-center justify-center gap-3 py-2 lg:w-40 lg:py-0" data-id="element-237" data-mp="8lee9q30eid2">
                <div className="hidden h-full w-px bg-mist lg:block" data-id="element-238" data-mp="djfyiec68nq4" />
                <div className="flex flex-col items-center gap-2 rounded-full border border-ink/10 bg-bone px-4 py-3 text-center" data-id="element-239" data-mp="dlsn3acfpwc5">
                  <UserIcon className="h-5 w-5 text-ink" aria-hidden="true" data-id="element-240" data-mp="2m392ty42rd27" />
                  <span className="font-display text-base text-ink" data-id="element-241" data-mp="2i1nvste81szx">Candidate</span>
                  <ArrowLeftRightIcon className="h-4 w-4 text-teal" aria-hidden="true" data-id="element-242" data-mp="2hzjf1av84zi8" />
                  <span className="max-w-[9rem] text-xs leading-snug text-graphite" data-id="element-243" data-mp="2d0q4nrb8homa">
                    The only channel between the two
                  </span>
                </div>
                <div className="hidden h-full w-px bg-mist lg:block" data-id="element-244" data-mp="35s9qzk34nisi" />
              </div> : null}
          </React.Fragment>)}
      </div>

      <div className="mt-20" data-id="element-245" data-mp="2vtaros494zi6">
        <div className="flex flex-wrap items-baseline justify-between gap-3" data-id="element-246" data-mp="2vu3trfs9n0tz">
          <h3 className="font-display text-3xl font-light text-ink" data-id="element-247" data-mp="1pcynoaer0neo">
            One session, 60–90 minutes
          </h3>
          <p className="font-mono text-xs uppercase tracking-[0.14em] text-graphite" data-id="element-248" data-mp="1kd3z4dig49ws">
            Browser sandbox · no install
          </p>
        </div>

        <div className="mt-8 flex h-3 w-full overflow-hidden rounded-full border border-mist bg-white" aria-hidden="true" data-id="element-249" data-mp="3890j1pwnh05j">
          {phaseWidths.map((w, i) => <span key={w} style={{
          width: w,
          paddingLeft: "40px"
        }} className={i === 2 ? 'block bg-teal' : i === 3 ? 'block bg-teal/50' : 'block bg-ink/25'} data-id="element-250" data-mp="2zbgd43nuefol" data-mpi={i} />)}
        </div>

        <ol className="mt-6 grid gap-px overflow-hidden rounded-2xl border border-mist bg-mist md:grid-cols-4" data-id="element-251" data-mp="3ryawsycu5b10">
          {sessionSteps.map(step => <li key={step.title} className="flex flex-col bg-bone p-6" data-id="element-252" data-mp="3vzw506efd14v">
              <span className="font-mono text-xs uppercase tracking-[0.12em] text-teal" data-id="element-253" data-mp="3i1g10n7y5oqf">
                {step.time}
              </span>
              <h4 className="mt-3 font-display text-xl text-ink" data-id="element-254" data-mp="3mxwnze3etx1x">{step.title}</h4>
              <p className="mt-2 text-[15px] leading-relaxed text-graphite" data-id="element-255" data-mp="26jwo234ke7eo">
                {step.body}
              </p>
            </li>)}
        </ol>
      </div>
    </Band>;
}